import { useId } from "react";
import { Plus, X } from "lucide-react";

import { SortableItem, SortableList } from "@/components/SortableList/SortableList";
import { cn } from "@/lib/utils";

import { MatrixGridProps } from "./types";

/**
 * Shared presentational matrix table shell.
 *
 * Plan of record: docs/plans/2026-09-04-shared-matrix-grid.md (rev 3, frozen).
 * Ports the studies `matrix_rating` / `matrix_categorical` table chrome into a
 * host-agnostic shell. Cells are decorative only (never persisted). Every
 * chrome string, callback, and testid comes from `props` -- this file must
 * never call `i18n.t` / `useTranslation`, and must never import from
 * `features/studies/questionnaire` or `pages/ThreadDetailPage`.
 */
export function MatrixGrid(props: MatrixGridProps) {
  const {
    rows,
    columns,
    cellKind,
    disabled = false,
    minRows,
    minColumns,
    maxRows,
    maxColumns,
    canEditRows = true,
    canEditColumns = true,
    minWidthClass,
    disabledReason,
    addColumnDisabledReason,
    revealControlsOnHover = false,
    renderRowLabel,
    renderColumnLabel,
    renderColumnMeta,
    cellAriaLabel,
    rowTestId,
    columnTestId,
    cellTestId,
    addRowTestId,
    addColumnTestId,
    removeRowTestId,
    removeColumnTestId,
    onRowAdd,
    onRowRemove,
    onColumnAdd,
    onColumnRemove,
    onRowMove,
    moveRowAriaLabel = "",
    moveRowTestId,
    addRowLabel,
    addRowAriaLabel,
    addColumnAriaLabel,
    removeRowAriaLabel,
    removeColumnAriaLabel,
  } = props;

  const showAddRow =
    canEditRows && (maxRows === undefined || rows.length < maxRows);
  const addRowDisabled = disabled;
  const addRowTitle = disabled ? disabledReason : undefined;
  const showRemoveRow = canEditRows;
  const removeRowDisabled = disabled || rows.length <= minRows;
  // Tooltip only when disabled by the `disabled` flag, never when the control
  // is merely disabled by the min-rows floor (Fix 1: a floor-disabled control
  // must not carry the workspace-lock reason as its tooltip).
  const removeRowTitle = disabled ? disabledReason : undefined;

  const showAddColumn = canEditColumns;
  const addColumnAtCap =
    maxColumns !== undefined && columns.length >= maxColumns;
  const addColumnDisabled = disabled || addColumnAtCap;
  const addColumnTitle = addColumnAtCap
    ? addColumnDisabledReason ?? disabledReason
    : disabled
      ? disabledReason
      : undefined;
  const addColumnLabel = addColumnAtCap
    ? addColumnDisabledReason ?? disabledReason ?? addColumnAriaLabel
    : addColumnAriaLabel;
  const showRemoveColumn = canEditColumns;
  const removeColumnDisabled = disabled || columns.length <= minColumns;
  // Same rule as removeRowTitle: never show disabledReason for a control that
  // is only floor-disabled (columns.length <= minColumns).
  const removeColumnTitle = disabled ? disabledReason : undefined;

  const addRevealClasses = revealControlsOnHover
    ? "opacity-0 transition-opacity duration-150 group-hover/matrix-grid:opacity-100 group-focus-within/matrix-grid:opacity-100"
    : "";
  const removeColumnRevealClasses = revealControlsOnHover
    ? "text-muted-foreground/0 group-hover/matrix-col:text-muted-foreground group-focus-within/matrix-col:text-muted-foreground"
    : "text-muted-foreground";
  const removeRowRevealClasses = revealControlsOnHover
    ? "text-muted-foreground/0 group-hover/matrix-row:text-muted-foreground group-focus-within/matrix-row:text-muted-foreground"
    : "text-muted-foreground";

  // Rows the host orders: all of them, while they are editable.
  const sortableRowCount =
    canEditRows && !disabled && onRowMove !== undefined ? rows.length : 0;
  const rowIdPrefix = useId();

  const handleRowAdd = () => onRowAdd?.();
  const handleRowRemove = (index: number) => onRowRemove?.(index);
  const handleColumnAdd = () => onColumnAdd?.();
  const handleColumnRemove = (index: number) => onColumnRemove?.(index);

  const columnMetaRow = renderColumnMeta ? (
    <tr>
      <th className="border-b-0 bg-transparent px-2 pb-1.5 pt-0" />
      {columns.map((column, columnIndex) => (
        <th
          key={`col-meta-${columnIndex}`}
          className="border-b-0 bg-transparent px-2 pb-1.5 pt-0 text-center"
        >
          {renderColumnMeta(column, columnIndex)}
        </th>
      ))}
    </tr>
  ) : null;

  return (
    <div className="group/matrix-grid">
      <div className="flex items-start">
        <div className="min-w-0 flex-1 overflow-x-auto">
          <SortableList
            idPrefix={rowIdPrefix}
            count={sortableRowCount}
            onMove={(from, to) => onRowMove?.(from, to)}
          >
            <table
              className={cn(
                "border-separate border-spacing-0 text-[12.5px]",
                minWidthClass,
              )}
            >
              <thead>
                <tr>
                  <th className="max-w-[22%] border-b border-border bg-transparent px-2 py-2 text-left font-medium" />
                  {columns.map((column, columnIndex) => (
                    <th
                      key={`col-${columnIndex}`}
                      scope="col"
                      data-testid={columnTestId(columnIndex)}
                      className="group/matrix-col min-w-[96px] border-b border-border bg-muted px-2 py-2 align-bottom"
                    >
                      <div
                        className="flex flex-col items-center gap-0.5"
                        title={column.label}
                      >
                        {renderColumnLabel(column, columnIndex)}
                        {showRemoveColumn ? (
                          <button
                            type="button"
                            className={cn(
                              "flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] transition-[background-color,color] hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40",
                              removeColumnRevealClasses,
                            )}
                            onClick={() => handleColumnRemove(columnIndex)}
                            disabled={removeColumnDisabled}
                            title={removeColumnTitle}
                            aria-label={removeColumnAriaLabel(columnIndex)}
                            data-testid={removeColumnTestId?.(columnIndex)}
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
                {columnMetaRow}
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <SortableItem
                    key={`row-${rowIndex}`}
                    idPrefix={rowIdPrefix}
                    index={rowIndex}
                    count={sortableRowCount}
                    handleLabel={moveRowAriaLabel}
                    handleTestId={moveRowTestId?.(rowIndex)}
                  >
                    {({ setNodeRef, style, handle }) => (
                      <tr
                        ref={setNodeRef}
                        style={style}
                        data-testid={rowTestId(rowIndex)}
                        className="group/matrix-row"
                      >
                        <th
                          scope="row"
                          className="min-w-[140px] border-b border-border/60 px-3 py-2.5 text-left font-normal"
                        >
                          <div className="flex items-center gap-1.5">
                            {handle}
                            <div className="min-w-0 flex-1" title={row.label}>
                              {renderRowLabel(row, rowIndex)}
                            </div>
                            {showRemoveRow ? (
                              <button
                                type="button"
                                className={cn(
                                  "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] transition-[background-color,color] hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40",
                                  removeRowRevealClasses,
                                )}
                                onClick={() => handleRowRemove(rowIndex)}
                                disabled={removeRowDisabled}
                                title={removeRowTitle}
                                aria-label={removeRowAriaLabel(rowIndex)}
                                data-testid={removeRowTestId?.(rowIndex)}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ) : null}
                          </div>
                        </th>
                        {columns.map((column, columnIndex) => (
                          <td
                            key={`cell-${rowIndex}-${columnIndex}`}
                            className="border-b border-border/60 px-2 py-2.5 text-center"
                          >
                            {cellKind === "dropdown" ? (
                              column.options.length === 0 ? (
                                <span
                                  data-testid={cellTestId(rowIndex, columnIndex)}
                                  className="mx-auto block h-9 w-full min-w-[92px] max-w-[132px] content-center text-center text-[12px] font-medium text-muted-foreground"
                                >
                                  {column.cellPlaceholder ?? ""}
                                </span>
                              ) : (
                                <select
                                  defaultValue=""
                                  disabled={disabled}
                                  tabIndex={-1}
                                  aria-label={cellAriaLabel(
                                    row,
                                    column,
                                    rowIndex,
                                    columnIndex,
                                  )}
                                  data-testid={cellTestId(rowIndex, columnIndex)}
                                  className="mx-auto block h-9 w-full min-w-[92px] max-w-[132px] rounded-[7px] border border-border bg-background px-2 text-center text-[12px] font-medium text-muted-foreground shadow-none transition-colors hover:border-accent-highlight focus:border-accent-highlight focus:outline-none dark:bg-card"
                                >
                                  <option value="" disabled />
                                  {column.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              )
                            ) : (
                              <span
                                aria-hidden
                                data-testid={cellTestId(rowIndex, columnIndex)}
                                className="mx-auto block h-4 w-4 rounded-full border-[1.5px] border-border bg-transparent"
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    )}
                  </SortableItem>
                ))}
              </tbody>
            </table>
          </SortableList>
        </div>
        {showAddColumn ? (
          <div className="flex w-9 shrink-0 items-start justify-center pt-[10px]">
            <button
              type="button"
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-[6px] border border-dashed border-border bg-background text-muted-foreground transition-colors duration-150 hover:border-accent-highlight hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40",
                addRevealClasses,
              )}
              onClick={handleColumnAdd}
              disabled={addColumnDisabled}
              title={addColumnTitle}
              aria-label={addColumnLabel}
              data-testid={addColumnTestId}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        ) : null}
      </div>
      {showAddRow ? (
        <button
          type="button"
          className={cn(
            "mt-2 inline-flex items-center gap-1.5 self-start rounded-[6px] border border-dashed border-border bg-transparent px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors duration-150 hover:border-accent-highlight hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40",
            addRevealClasses,
          )}
          onClick={handleRowAdd}
          disabled={addRowDisabled}
          title={addRowTitle}
          aria-label={addRowAriaLabel}
          data-testid={addRowTestId}
        >
          <Plus className="h-3 w-3" />
          {addRowLabel}
        </button>
      ) : null}
    </div>
  );
}

